import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Breadcrumb,
  Card,
  Button,
  Table,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  message,
} from 'antd';
import * as Icon from 'react-feather';
import { Link } from 'react-router-dom';
import {
  communityTagActionStart,
  communityTagActionError,
  getAllCommunityTagsAction,
  createCommunityTagAction,
  deleteCommunityTagAction,
} from '../../core/data/redux/modules/communityTag/actions';
import {
  getAllCommunityTags,
  createCommunityTag,
  deleteCommunityTag,
} from '../../services/adminServices/communityTag';

const { Title, Text } = Typography;

const CommunityTagsManager = () => {
  const dispatch = useDispatch();
  const { communityTags, loading } = useSelector(
    (state: any) =>
      state.communityTag || {
        communityTags: [],
        loading: false,
        error: null,
      },
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchCommunityTags();
  }, []);

  const fetchCommunityTags = async () => {
    dispatch(communityTagActionStart());
    try {
      const res: any = await getAllCommunityTags();
      if (res.error) throw new Error(res.error || 'Error fetching community tags');
      const tags = res.data?.items || res.data || [];
      dispatch(getAllCommunityTagsAction(tags));
    } catch (err: any) {
      dispatch(communityTagActionError(err.message));
      message.error(err.message);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      dispatch(communityTagActionStart());
      const res: any = await createCommunityTag(values);
      if (res.error) throw new Error(res.error || 'Error creating community tag');
      dispatch(createCommunityTagAction(res.data));
      message.success('Community tag added successfully');
      setIsModalVisible(false);
      form.resetFields();
      await fetchCommunityTags();
    } catch (err: any) {
      dispatch(communityTagActionError(err.message));
      message.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const res: any = await deleteCommunityTag(id);
      if (res.error) throw new Error(res.error || 'Error removing community tag');
      dispatch(deleteCommunityTagAction(id));
      message.success('Community tag removed successfully');
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'Community',
      dataIndex: 'community_id',
      key: 'community_id',
      render: (_: any, record: any) =>
        record.community?.name || record.community_id,
    },
    {
      title: 'Tag',
      dataIndex: 'tag_id',
      key: 'tag_id',
      render: (_: any, record: any) => record.tag?.name || record.tag_id,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string | undefined) =>
        value ? new Date(value).toLocaleString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<Icon.Trash2 size={16} />}
            size="small"
            danger
            loading={deletingId === record.id}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/community-management">Community Management</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Community Tags</Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Community Tags
              </Title>
              <Text type="secondary">
                Attach or remove tags assigned to specific communities.
              </Text>
            </div>
            <Button
              type="primary"
              icon={<Icon.Plus size={16} />}
              onClick={() => setIsModalVisible(true)}
            >
              Add Tag to Community
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={communityTags}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Modal
          title="Attach Tag to Community"
          open={isModalVisible}
          onOk={handleCreate}
          onCancel={() => setIsModalVisible(false)}
          okText="Save"
          confirmLoading={creating}
        >
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Community ID"
              name="community_id"
              rules={[{ required: true, message: 'Please enter a community ID' }]}
            >
              <Input placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000" />
            </Form.Item>
            <Form.Item
              label="Tag ID"
              name="tag_id"
              rules={[{ required: true, message: 'Please enter a tag ID' }]}
            >
              <Input placeholder="e.g. 123e4567-e89b-12d3-a456-426614174abc" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CommunityTagsManager;

